// TMDB API Service
import { getTMDBApiKey, getTMDBBearerToken } from './settingsService';
import { getCachedTMDBData, setCachedTMDBData } from './tmdbCache';

const TMDB_BASE_URL = 'https://api.themoviedb.org/3';
const TMDB_V4_BASE_URL = 'https://api.themoviedb.org/4';
const TMDB_IMAGE_BASE_URL = 'https://image.tmdb.org/t/p';

// Cache API key to prevent repeated database calls
let cachedApiKey = null;
let apiKeyPromise = null;

// Get API key from multiple sources (cached version)
const getApiKey = async () => {
  // Return cached key if available
  if (cachedApiKey) {
    return cachedApiKey;
  }
  
  // If already fetching, return the same promise
  if (apiKeyPromise) {
    return apiKeyPromise;
  }
  
  // Create promise to fetch API key
  apiKeyPromise = (async () => {
    // Try environment variable first
    if (process.env.REACT_APP_TMDB_API_KEY) {
      cachedApiKey = process.env.REACT_APP_TMDB_API_KEY;
      return cachedApiKey;
    }
    
    // Try window variable (set by settings component)
    if (window.REACT_APP_TMDB_API_KEY) {
      cachedApiKey = window.REACT_APP_TMDB_API_KEY;
      return cachedApiKey;
    }
    
    // Try localStorage
    if (typeof window !== 'undefined' && window.localStorage) {
      const savedKey = localStorage.getItem('tmdb_api_key');
      if (savedKey) {
        cachedApiKey = savedKey;
        return cachedApiKey;
      }
    }
    
    // Try server-side storage (Supabase) - ONLY as last resort
    try {
      const { data: serverKey } = await getTMDBApiKey();
      if (serverKey) {
        cachedApiKey = serverKey;
        return cachedApiKey;
      }
    } catch (error) {
      console.warn('Could not fetch TMDB API key from server:', error.message);
    }
    
    // Fallback API key for testing (you can remove this in production)
    cachedApiKey = 'e133a91e37d0d8de15ab3ab168097c45';
    return cachedApiKey;
  })();
  
  const result = await apiKeyPromise;
  apiKeyPromise = null; // Reset promise after completion
  return result;
};

// Get Bearer token for v4 API (read access token, async version)
const getBearerToken = async () => {
  console.log('🔍 getBearerToken: Checking all sources...');
  
  // Try environment variable first
  if (process.env.REACT_APP_TMDB_BEARER_TOKEN) {
    console.log('✅ Found Bearer token in environment variable');
    return process.env.REACT_APP_TMDB_BEARER_TOKEN;
  }
  console.log('❌ No Bearer token in environment variable');
  
  // Try server-side storage (Supabase)
  try {
    console.log('🔍 Checking server-side storage...');
    const { data: serverToken } = await getTMDBBearerToken();
    if (serverToken) {
      console.log('✅ Found Bearer token in server storage');
      return serverToken;
    }
    console.log('❌ No Bearer token in server storage');
  } catch (error) {
    console.warn('❌ Could not fetch TMDB Bearer token from server:', error.message);
  }
  
  // Try localStorage
  if (typeof window !== 'undefined' && window.localStorage) {
    const savedToken = localStorage.getItem('tmdb_bearer_token');
    if (savedToken) {
      console.log('✅ Found Bearer token in localStorage');
      return savedToken;
    }
    console.log('❌ No Bearer token in localStorage');
  }
  
  // Try window variable (set by settings component)
  if (window.REACT_APP_TMDB_BEARER_TOKEN) {
    console.log('✅ Found Bearer token in window variable');
    return window.REACT_APP_TMDB_BEARER_TOKEN;
  }
  console.log('❌ No Bearer token in window variable');
  
  // Fallback read access token for testing
  console.log('🔄 Using fallback Bearer token for testing');
  return 'eyJhbGciOiJIUzI1NiJ9.eyJhdWQiOiJlMTMzYTkxZTM3ZDBkOGRlMTVhYjNhYjE2ODA5N2M0NSIsIm5iZiI6MTc1Njg2MDQzOC4zMjE5OTk4LCJzdWIiOiI2OGI3OTAxNmYyYTY3YWJhOWU0ZTQ2NDkiLCJzY29wZXMiOlsiYXBpX3JlYWQiXSwidmVyc2lvbiI6MX0.ETKgluElwDwJiRD4znCrlynfIqSzE1Puv8SXUxV63DE';
};

// Search for movies by title and year
export const searchMovies = async (title, year = null) => {
  const apiKey = await getApiKey();
  if (!apiKey) {
    console.warn('TMDB API key not found');
    return null;
  }

  try {
    const yearParam = year ? `&year=${year}` : '';
    const response = await fetch(
      `${TMDB_BASE_URL}/search/movie?api_key=${apiKey}&query=${encodeURIComponent(title)}${yearParam}`
    );
    
    if (!response.ok) {
      throw new Error(`TMDB API error: ${response.status}`);
    }
    
    const data = await response.json();
    return data.results;
  } catch (error) {
    console.error('Error searching movies:', error);
    return null;
  }
};

// Get detailed movie information (with caching)
export const getMovieDetails = async (movieId) => {
  // Check cache first using movieId as a special cache key
  const cacheKey = `id:${movieId}`;
  const cachedData = await getCachedTMDBData(cacheKey);
  if (cachedData) {
    return cachedData;
  }

  const apiKey = await getApiKey();
  if (!apiKey) {
    console.warn('TMDB API key not found');
    return null;
  }

  try {
    const response = await fetch(
      `${TMDB_BASE_URL}/movie/${movieId}?api_key=${apiKey}&append_to_response=credits,videos,watch/providers`
    );
    
    if (!response.ok) {
      throw new Error(`TMDB API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Cache the result
    await setCachedTMDBData(cacheKey, null, data);
    
    return data;
  } catch (error) {
    console.error('Error getting movie details:', error);
    return null;
  }
};

// Get streaming providers for a movie (US region)
export const getStreamingProviders = async (movieId) => {
  const apiKey = await getApiKey();
  if (!apiKey) {
    console.warn('TMDB API key not found');
    return null;
  }

  try {
    const response = await fetch(
      `${TMDB_BASE_URL}/movie/${movieId}/watch/providers?api_key=${apiKey}`
    );
    
    if (!response.ok) {
      throw new Error(`TMDB API error: ${response.status}`);
    }
    
    const data = await response.json();
    return data.results?.US || null;
  } catch (error) {
    console.error('Error getting streaming providers:', error);
    return null;
  }
};

// Helper functions for image URLs
export const getPosterUrl = (posterPath, size = 'w500') => {
  if (!posterPath) return null;
  return `${TMDB_IMAGE_BASE_URL}/${size}${posterPath}`;
};

export const getBackdropUrl = (backdropPath, size = 'w1280') => {
  if (!backdropPath) return null;
  return `${TMDB_IMAGE_BASE_URL}/${size}${backdropPath}`;
};

/* ---------------------------------------------------------------------------
 * Responsive poster sizing
 *
 * posterUrl is built once at extraction time and then persisted - the rows in
 * movie_collection and tmdb_cache already have `/t/p/w500/` baked into them.
 * So we cannot change poster size by editing getPosterUrl's default; that would
 * only affect movies fetched from now on.
 *
 * Instead we recover the bare image path from whatever URL we were given and
 * re-request the same image at a size appropriate to where it is rendered. That
 * works for already-stored data with no migration and no cache bust.
 *
 * Measured cost of one poster (average over sample images from this library):
 *   w92 4.5 KB | w154 9.4 KB | w185 11.9 KB | w342 31.8 KB | w500 53.6 KB
 * ------------------------------------------------------------------------- */

// Pull "/abc123.jpg" out of a full TMDB URL, or pass a bare path straight back.
// Returns null for anything that isn't a TMDB image (placeholder movies may
// carry a custom or data: URL, which must be left completely alone).
export const getPosterPath = (urlOrPath) => {
  if (!urlOrPath || typeof urlOrPath !== 'string') return null;

  const fromFullUrl = /image\.tmdb\.org\/t\/p\/[^/]+(\/[^/?#]+)/.exec(urlOrPath);
  if (fromFullUrl) return fromFullUrl[1];

  // Bare TMDB path, e.g. "/abc123.jpg"
  if (/^\/[A-Za-z0-9]+\.(jpg|jpeg|png|webp)$/i.test(urlOrPath)) return urlOrPath;

  return null;
};

// Re-request a poster at a specific size. Non-TMDB URLs pass through untouched.
export const resizePosterUrl = (urlOrPath, size = 'w342') => {
  const path = getPosterPath(urlOrPath);
  if (!path) return urlOrPath || null;
  return `${TMDB_IMAGE_BASE_URL}/${size}${path}`;
};

// Build a srcSet so the browser can pick by viewport AND device pixel ratio.
// Widths are deliberately capped at 500 - the size everything requests today -
// so this can only ever transfer the same or fewer bytes, never more.
export const buildPosterSrcSet = (urlOrPath, widths = [185, 342, 500]) => {
  const path = getPosterPath(urlOrPath);
  if (!path) return undefined;
  return widths.map((w) => `${TMDB_IMAGE_BASE_URL}/w${w}${path} ${w}w`).join(', ');
};

// Get YouTube trailer URL
export const getTrailerUrl = (videos) => {
  if (!videos || !videos.results) return null;
  
  const trailer = videos.results.find(
    video => video.type === 'Trailer' && video.site === 'YouTube'
  );
  
  if (trailer) {
    return `https://www.youtube.com/watch?v=${trailer.key}`;
  }
  
  return null;
};

// Extract key information from movie details
export const extractMovieInfo = (movieDetails) => {
  if (!movieDetails) return null;

  const director = movieDetails.credits?.crew?.find(person => person.job === 'Director');
  const writers = movieDetails.credits?.crew?.filter(person => 
    person.job === 'Writer' || person.job === 'Screenplay' || person.job === 'Story'
  ) || [];
  const topCast = movieDetails.credits?.cast?.slice(0, 5) || [];

  return {
    id: movieDetails.id,
    title: movieDetails.title,
    releaseDate: movieDetails.release_date,
    runtime: movieDetails.runtime,
    plot: movieDetails.overview,
    posterUrl: getPosterUrl(movieDetails.poster_path),
    backdropUrl: getBackdropUrl(movieDetails.backdrop_path),
    trailerUrl: getTrailerUrl(movieDetails.videos),
    originalLanguage: movieDetails.original_language,
    director: director ? director.name : null,
    writers: writers.map(writer => writer.name),
    stars: topCast.map(actor => actor.name),
    genres: movieDetails.genres?.map(genre => genre.name) || [],
    rating: movieDetails.vote_average,
    voteCount: movieDetails.vote_count,
    streamingProviders: null // Will be populated separately if needed
  };
};

// The TMDB id a movie record carries, if any. Imported movies store it as
// `tmdbId`; their `id` is also `tmdb_<n>`, so that prefix is accepted as a
// second source. Placeholders have neither and get null.
export const tmdbIdOf = (movie) => {
  if (!movie) return null;
  const direct = Number(movie.tmdbId);
  if (Number.isInteger(direct) && direct > 0) return direct;
  const m = /^tmdb_(\d+)$/.exec(String(movie.id || ''));
  return m ? Number(m[1]) : null;
};

// Find the TMDB record for a movie. With a known id this is a direct fetch,
// cached under that id, and the title is never searched. Without one it is a
// title-and-year search, which is a guess: whichever same-year namesake TMDB
// ranks first that day wins. "November" (2017) became "November Criminals"
// (2017) that way, a week after a correct import, when its cached guess
// expired and TMDB's popularity order had shifted. Every imported movie has
// an id, so in practice the search path now serves nothing but placeholders.
export const findBestMovieMatch = async (title, year = null, tmdbId = null) => {
  if (tmdbId) {
    // No fallback to the title search on failure: a missing poster for one
    // session is better than confidently showing the wrong film. The movie
    // record keeps its own poster URL for exactly that case.
    return getMovieDetails(tmdbId);
  }

  // Check cache first
  const cachedData = await getCachedTMDBData(title, year);
  if (cachedData) {
    return cachedData;
  }

  const searchResults = await searchMovies(title, year);
  if (!searchResults || searchResults.length === 0) {
    return null;
  }

  let selectedMovie = null;

  // If we have a year, try to find exact year match first
  if (year) {
    const exactYearMatch = searchResults.find(movie => {
      const movieYear = movie.release_date ? new Date(movie.release_date).getFullYear() : null;
      return movieYear === parseInt(year);
    });
    
    if (exactYearMatch) {
      selectedMovie = await getMovieDetails(exactYearMatch.id);
    }
  }
  
  // Otherwise, return the first (most popular) result
  if (!selectedMovie) {
    selectedMovie = await getMovieDetails(searchResults[0].id);
  }

  // Cache the result
  if (selectedMovie) {
    await setCachedTMDBData(title, year, selectedMovie);
  }

  return selectedMovie;
};

// Batch process multiple movies
export const enrichMoviesWithTMDBData = async (movies) => {
  const enrichedMovies = [];
  
  for (const movie of movies) {
    try {
      const tmdbData = await findBestMovieMatch(movie.title, movie.year, tmdbIdOf(movie));
      const movieInfo = extractMovieInfo(tmdbData);
      
      enrichedMovies.push({
        ...movie,
        tmdbData: movieInfo,
        // Update runtime if TMDB has it and original doesn't
        runtime: movieInfo?.runtime || movie.runtime,
        // Add additional fields
        plot: movieInfo?.plot,
        posterUrl: movieInfo?.posterUrl,
        trailerUrl: movieInfo?.trailerUrl,
        director: movieInfo?.director,
        writers: movieInfo?.writers,
        stars: movieInfo?.stars,
        originalLanguage: movieInfo?.originalLanguage,
        genres: movieInfo?.genres,
        tmdbRating: movieInfo?.rating
      });
      
      // Add small delay to respect API rate limits
      await new Promise(resolve => setTimeout(resolve, 250));
    } catch (error) {
      console.error(`Error enriching movie ${movie.title}:`, error);
      enrichedMovies.push(movie); // Keep original if enrichment fails
    }
  }
  
  return enrichedMovies;
};

// Get user's account lists from TMDB v4 API
export const getUserAccountLists = async (page = 1) => {
  console.log('🔍 getUserAccountLists called, page:', page);
  
  const bearerToken = await getBearerToken();
  console.log('🔑 Bearer token retrieved:', bearerToken ? `${bearerToken.substring(0, 20)}...` : 'null');
  
  if (!bearerToken) {
    console.warn('❌ TMDB Bearer token not found');
    return [];
  }
  
  try {
    // The v4 API documentation shows we need to get account info first
    // Let's try the correct v4 endpoints based on the documentation
    
    console.log('🔍 Trying to get account info for v4 API...');
    
    // Try multiple possible account endpoints
    const accountEndpoints = [
      `${TMDB_V4_BASE_URL}/account`,
      `https://api.themoviedb.org/4/auth/account`,
      `https://api.themoviedb.org/4/user/account`
    ];
    
    let accountObjectId = null;
    
    for (const endpoint of accountEndpoints) {
      console.log(`🔍 Trying account endpoint: ${endpoint}`);
      try {
        const accountResponse = await fetch(endpoint, {
          headers: {
            'Authorization': `Bearer ${bearerToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        console.log(`📡 Response status for ${endpoint}:`, accountResponse.status, accountResponse.statusText);
        
        if (accountResponse.ok) {
          const accountData = await accountResponse.json();
          console.log('👤 Account data received:', accountData);
          
          accountObjectId = accountData.account_object_id || accountData.object_id || accountData.id;
          if (accountObjectId) {
            console.log('✅ Found account object ID:', accountObjectId);
            break;
          }
        } else {
          const errorText = await accountResponse.text();
          console.log(`❌ Error from ${endpoint}:`, errorText);
        }
      } catch (err) {
        console.log(`❌ Exception from ${endpoint}:`, err.message);
      }
    }
    
    if (!accountObjectId) {
      console.warn('❌ Could not find account object ID from any endpoint');
      console.log('🔍 Trying with example account ID from documentation as fallback...');
      // Use the example from the documentation as a last resort
      accountObjectId = '68b79016f2a67aba9e4e4649';
    }

    // Now try to get the user's lists using the account object ID
    console.log('🔍 Fetching user lists from:', `${TMDB_V4_BASE_URL}/account/${accountObjectId}/lists?page=${page}`);
    const response = await fetch(
      `${TMDB_V4_BASE_URL}/account/${accountObjectId}/lists?page=${page}`,
      {
        headers: {
          'Authorization': `Bearer ${bearerToken}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('📡 Lists response status:', response.status, response.statusText);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Lists API error response:', errorText);
      throw new Error(`TMDB v4 API error: ${response.status} - ${errorText}`);
    }
    
    const data = await response.json();
    console.log('📋 Lists data received:', data);
    console.log('📋 Number of lists found:', data.results ? data.results.length : 0);
    
    return data.results || [];
  } catch (error) {
    console.error('Error fetching user account lists:', error);
    return [];
  }
};

// Search functionality now searches within user's own lists
export const searchLists = async (query) => {
  console.log('🔍 searchLists called with query:', query);
  
  const userLists = await getUserAccountLists();
  console.log('📋 getUserAccountLists returned:', userLists);
  
  if (!query) {
    console.log('🔍 No query provided, returning all user lists:', userLists.length);
    return userLists;
  }
  
  // Filter user's lists by search query
  const searchTerm = query.toLowerCase();
  console.log('🔍 Filtering lists with search term:', searchTerm);
  
  const filteredLists = userLists.filter(list => {
    const nameMatch = list.name?.toLowerCase().includes(searchTerm);
    const descMatch = list.description?.toLowerCase().includes(searchTerm);
    console.log(`📋 List "${list.name}": nameMatch=${nameMatch}, descMatch=${descMatch}`);
    return nameMatch || descMatch;
  });
  
  console.log('✅ Filtered results:', filteredLists.length, filteredLists);
  return filteredLists;
};



export const getListDetails = async (listId) => {
  const bearerToken = await getBearerToken();
  if (!bearerToken) {
    console.warn('❌ TMDB Bearer token not found');
    return null;
  }
  
  if (!listId) return null;
  
  try {
    console.log('🔍 Fetching list details via v4 API for list:', listId);
    
    // First get the basic list info
    const response = await fetch(
      `${TMDB_V4_BASE_URL}/list/${listId}`,
      {
        headers: {
          'Authorization': `Bearer ${bearerToken}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('📡 List details response status:', response.status, response.statusText);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ List details API error response:', errorText);
      throw new Error(`TMDB v4 API error: ${response.status} - ${errorText}`);
    }
    
    const data = await response.json();
    console.log('📋 List details received:', data);
    console.log('📊 Total results in list:', data.total_results);
    console.log('📄 Total pages:', data.total_pages);
    
    let allItems = data.results || [];
    const totalPages = data.total_pages || 1;
    
    // If there are multiple pages, fetch all of them
    if (totalPages > 1) {
      console.log('🔄 Fetching additional pages...');
      
      const pagePromises = [];
      for (let page = 2; page <= Math.min(totalPages, 10); page++) { // Limit to 10 pages max (200 movies)
        pagePromises.push(
          fetch(`${TMDB_V4_BASE_URL}/list/${listId}?page=${page}`, {
            headers: {
              'Authorization': `Bearer ${bearerToken}`,
              'Content-Type': 'application/json'
            }
          })
        );
      }
      
      const pageResponses = await Promise.all(pagePromises);
      
      for (let i = 0; i < pageResponses.length; i++) {
        const pageResponse = pageResponses[i];
        const pageNum = i + 2;
        
        console.log(`📡 Page ${pageNum} response status:`, pageResponse.status);
        
        if (pageResponse.ok) {
          const pageData = await pageResponse.json();
          console.log(`📋 Page ${pageNum} items:`, pageData.results?.length || 0);
          allItems = allItems.concat(pageData.results || []);
        } else {
          console.warn(`❌ Failed to fetch page ${pageNum}:`, pageResponse.status);
        }
      }
    }
    
    console.log('✅ Total items fetched:', allItems.length);
    
    // Transform the v4 response to match our expected format
    return {
      id: data.id,
      name: data.name,
      description: data.description || data.overview || 'No description available',
      item_count: data.total_results || allItems.length || 0,
      created_by: data.created_by || { username: 'Unknown' },
      items: allItems
    };
  } catch (error) {
    console.error('❌ Error fetching list details:', error);
    return null;
  }
};

// Convert TMDB list movies to our movie format
export const convertTMDBListToMovies = (tmdbList) => {
  if (!tmdbList || !tmdbList.items) return [];
  
  return tmdbList.items.map(tmdbMovie => {
    const year = tmdbMovie.release_date ? new Date(tmdbMovie.release_date).getFullYear() : null;
    
    return {
      id: `tmdb_${tmdbMovie.id}`, // Prefix to avoid conflicts with existing movies
      title: tmdbMovie.title,
      year: year,
      runtime: tmdbMovie.runtime || 120, // Default runtime if not available
      color: getRandomMovieColor(),
      // TMDB data
      tmdbId: tmdbMovie.id,
      plot: tmdbMovie.overview,
      posterUrl: getPosterUrl(tmdbMovie.poster_path),
      backdropUrl: getBackdropUrl(tmdbMovie.backdrop_path),
      genres: tmdbMovie.genre_ids ? [] : [], // Would need genre lookup for full names
      tmdbRating: tmdbMovie.vote_average,
      originalLanguage: tmdbMovie.original_language,
      // Source tracking
      importedFrom: 'tmdb_list',
      importedListId: tmdbList.id,
      importedListName: tmdbList.name
    };
  });
};

// Helper function to get random movie colors (same as used elsewhere in the app)
const getRandomMovieColor = () => {
  const colors = [
    'bg-red-600', 'bg-blue-600', 'bg-green-600', 'bg-yellow-600', 
    'bg-purple-600', 'bg-pink-600', 'bg-indigo-600', 'bg-gray-600',
    'bg-orange-600', 'bg-teal-600', 'bg-cyan-600', 'bg-lime-600'
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

// Extract TMDB movie ID from URL
export const extractTMDBMovieId = (url) => {
  if (!url || typeof url !== 'string') return null;
  
  // Match various TMDB URL formats:
  // https://www.themoviedb.org/movie/694-the-shining
  // https://themoviedb.org/movie/694
  // www.themoviedb.org/movie/694-the-shining
  // themoviedb.org/movie/694
  const tmdbRegex = /(?:https?:\/\/)?(?:www\.)?themoviedb\.org\/movie\/(\d+)(?:-[^/?]*)?(?:\?.*)?$/i;
  const match = url.match(tmdbRegex);
  
  return match ? parseInt(match[1], 10) : null;
};

// Add movie from TMDB URL
export const addMovieFromTMDBUrl = async (url) => {
  const movieId = extractTMDBMovieId(url);
  if (!movieId) {
    throw new Error('Invalid TMDB URL. Please provide a valid movie URL like: https://www.themoviedb.org/movie/694-the-shining');
  }
  
  try {
    const movieDetails = await getMovieDetails(movieId);
    if (!movieDetails) {
      throw new Error('Movie not found on TMDB');
    }
    
    const movieInfo = extractMovieInfo(movieDetails);
    if (!movieInfo) {
      throw new Error('Failed to extract movie information');
    }
    
    // Convert to our movie format
    const movie = {
      id: `tmdb_${movieInfo.id}`,
      title: movieInfo.title,
      year: movieInfo.releaseDate ? new Date(movieInfo.releaseDate).getFullYear() : null,
      runtime: movieInfo.runtime || 120,
      color: getRandomMovieColor(),
      // TMDB data
      tmdbId: movieInfo.id,
      plot: movieInfo.plot,
      posterUrl: movieInfo.posterUrl,
      backdropUrl: movieInfo.backdropUrl,
      trailerUrl: movieInfo.trailerUrl,
      genres: movieInfo.genres,
      tmdbRating: movieInfo.rating,
      voteCount: movieInfo.voteCount,
      originalLanguage: movieInfo.originalLanguage,
      director: movieInfo.director,
      writers: movieInfo.writers,
      stars: movieInfo.stars,
      // Source tracking
      importedFrom: 'tmdb_url',
      importedUrl: url
    };
    
    return movie;
  } catch (error) {
    console.error('Error adding movie from TMDB URL:', error);
    throw error;
  }
};
